import { PageController } from '@client/components/_core/layout/PageController';
import { InlineQueryError } from '@client/components/_ui/feedback/InlineQueryError';
import { Loading } from '@client/components/_ui/feedback/Loading';
import { HostedCard, HostedNotice, HostedShell } from '@client/components/lodgement/hosted/HostedShell';
import { HostedSummaryView, POLL_MS } from '@client/components/lodgement/hosted/HostedSummary';
import { trpcCodeOf } from '@client/components/lodgement/hosted/lodgement-hosted';
import { trpcOptions } from '@client/trpc/client';
import { useQuery } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { zodValidator } from '@tanstack/zod-adapter';
import { z } from 'zod';

const searchSchema = z.object({
    summaryToken: z
        .string()
        .regex(/^[0-9a-f]{32}$/)
        .optional()
        .catch(undefined),
});

/**
 * Opened from the confirmation email: read-only, authenticated by the email's token, which travels
 * as `?summaryToken=` so the telemetry redaction strips it before any URL leaves the browser.
 */
export const Route = createFileRoute('/lodge/summary/$publicId')({
    validateSearch: zodValidator(searchSchema),
    component: LodgeSummary,
});

function LodgeSummary() {
    const { publicId } = Route.useParams();
    const { summaryToken: token } = Route.useSearch();
    const query = useQuery(
        trpcOptions.lodgement.getSummary.queryOptions(
            { publicId, token: token ?? '' },
            {
                enabled: token !== undefined,
                retry: false,
                refetchInterval: (state) => {
                    const status = state.state.data?.status;
                    return status === 'SUBMITTED' || status === 'PROCESSING' ? POLL_MS : false;
                },
                meta: { errorSurface: 'inline' },
            },
        ),
    );
    const invalid =
        token === undefined || trpcCodeOf(query.error) === 'NOT_FOUND' || trpcCodeOf(query.error) === 'BAD_REQUEST';

    return (
        <PageController route={Route}>
            <HostedShell branding={{ orgName: null, logoUrl: null, title: 'Your claim' }}>
                {invalid ? (
                    <HostedNotice title="This link isn't valid">
                        Open the link from your confirmation email, or contact the organisation handling your claim.
                    </HostedNotice>
                ) : query.data ? (
                    <HostedSummaryView status={query.data} refreshError={query.error ?? undefined} />
                ) : query.error ? (
                    <InlineQueryError
                        error={query.error}
                        title="Unable to load your claim"
                        onRetry={() => void query.refetch()}
                        retrying={query.isFetching}
                    />
                ) : (
                    <HostedCard>
                        <Loading description="Loading your claim" />
                    </HostedCard>
                )}
            </HostedShell>
        </PageController>
    );
}
